import {GanttChartXAxis} from '@hconnect/ganttchart/src/modules/GanttChartXAxis'
import {LabelRegular} from '@hm/fundamental'
import {HeatZonesView} from '@kilnfeature/modules/heatZones/HeatZonesView'
import {StaticKilnSvgWrapper} from '@widgets/kms'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {KilnOverviewDrillingsChart} from './KilnOverviewDrillingsChart'
import {DriveSvg, StationSvg, KilnShellSvg} from './StaticSvg'

import {useShutdowns} from '@refra/modules/common/providers'
import {useKilnSettings} from '@refra/modules/refra-planning/providers'

const DEFAULT_STEP_WIDTH = 15

export const KilnOverviewCardBody: React.FC = () => {
  const {t} = useTranslation()

  const {kilnConfig} = useKilnSettings()
  const {activeShutdowns} = useShutdowns()

  const heatZones = useMemo(() => kilnConfig.payload.layout.heatZones ?? [], [kilnConfig])
  const kilnLength = Number(kilnConfig.payload.configuration.dimensions.length)

  const xAxis = Array.from({length: kilnLength}, (_, index) => index)
  const ganttContainerRef = useRef<HTMLDivElement>(null)
  const [stepWidth, setStepWidth] = useState(DEFAULT_STEP_WIDTH)
  const [containerWidth, setContainerWidth] = useState<number>()
  const canvasWidth = useMemo(() => stepWidth * xAxis.length, [stepWidth, xAxis])

  useEffect(() => {
    const handleResize = () => {
      if (ganttContainerRef.current) {
        setContainerWidth(ganttContainerRef.current.clientWidth)
        setStepWidth(ganttContainerRef.current.clientWidth / xAxis.length)
      }
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [xAxis.length])

  return (
    <div className="gap-8 flex flex-col w-full h-full" data-cy="kiln_overview_card_body">
      <div className="w-full flex">
        <div className="max-w-[88px] flex flex-1 items-center">
          <LabelRegular data-cy="heat_zones_title">
            {t('refraPlanning.kilnOverview.heatZones')}
          </LabelRegular>
        </div>
        <div className="mr-6 flex-1">
          <HeatZonesView
            heatZones={heatZones}
            kilnLength={kilnLength}
            hideXAxis={true}
            zoneHeight={24}
            hideDetails={true}
            maxWidth={containerWidth}
          />
        </div>
      </div>
      <div className="w-full flex">
        <div className="max-w-[88px] flex flex-1 items-center">
          <LabelRegular data-cy="kiln_svg_wrapper_title">
            {t('refraPlanning.kilnOutlet')}
          </LabelRegular>
        </div>
        <div
          ref={ganttContainerRef}
          className="mr-6 flex-1"
          data-cy="kiln_overview_card_kiln_svg_wrapper"
        >
          {kilnConfig.payload.layout && (
            <StaticKilnSvgWrapper
              kilnLength={kilnLength}
              kilnLayout={kilnConfig.payload.layout}
              customSvgPreviews={{station: StationSvg, drive: DriveSvg, kilnShell: KilnShellSvg}}
              containerWidth={containerWidth}
            />
          )}
        </div>
      </div>
      <div className="w-full flex">
        <div className="max-w-[88px] flex-1 flex items-center">
          <LabelRegular data-cy="x_axis_title">{t('refraPlanning.xAxisLabel')}</LabelRegular>
        </div>
        <div className="flex-1 mr-6" data-cy="kiln_overview_card_x_axis">
          <GanttChartXAxis
            canvasWidth={canvasWidth}
            kilnLength={kilnLength}
            stepWidth={stepWidth}
            yAxisLabelWidth={0}
          />
        </div>
      </div>
      {activeShutdowns.length > 0 && (
        <div className="w-full flex">
          <div className="max-w-[88px] flex flex-1 items-center">
            <LabelRegular data-cy="drillings_title">{t('refraPlanning.drillings')}</LabelRegular>
          </div>
          <div className="mr-6 flex-1">
            <KilnOverviewDrillingsChart
              shutdown={activeShutdowns[0]}
              kilnLength={kilnLength}
              stepWidth={stepWidth}
              xAxis={xAxis}
            />
          </div>
        </div>
      )}
    </div>
  )
}
